import { getContentfulDiscountCodes } from "../integrations/contentful.delivery";
import { CacheManager } from "../foundation/cache-manager";
import { getDomain, formatAsHundredths } from "../foundation/utilities";
// Migration-time verbose tracing per user request — remove after verification
const debug = false;
// Use CacheManager with domain + namespace to prevent multi-tenant cache collisions
const cartCache = new CacheManager({ mode: 'local', domain: getDomain(), namespace: 'checkout' });
/* ========== LOCALSTORAGE KEYS ========== */
export const shoppingCartKey = "pixelvividCart";
export const shippingInfoKey = "pixelvividCartShipping";
export const discountCodesKey = "pixelvividDiscountCodes";
export const checkoutInfoKey = "pixelvividCartCheckout";
/* ======================================= */
/* ========== BACKEND FUNCTIONS ========== */
/* ======================================= */
/* ========== SHOPPING CART FUNCTIONS ========== */
export function getCart() {
    if (debug)
        console.debug('ShoppingCart:getCart -> using CacheManager.get', shoppingCartKey);
    // Use CacheManager as the single source-of-truth. Legacy raw-localStorage fallbacks
    // were removed after migration completed — callers should use CacheManager APIs.
    const cached = cartCache.get(shoppingCartKey);
    if (cached)
        return cached;
    // No cart found -> empty
    return [];
}
export function setCart(shoppingCartJSON) {
    if (debug)
        console.debug('ShoppingCart:setCart -> using CacheManager.set', shoppingCartKey, shoppingCartJSON);
    cartCache.set(shoppingCartKey, shoppingCartJSON);
    // preserve observable contract (storage event) for listeners
    window.dispatchEvent(new Event('storage'));
}
export function alreadyInCart(cart, itemID) {
    for (const key in cart) {
        const item = cart[key];
        if (typeof item === 'object' && item !== null && Object.prototype.hasOwnProperty.call(item, 'itemID') && item.itemID == itemID) {
            return true;
        }
    }
    return false;
}
export function increaseQuantityCart(cart, itemID) {
    for (const key in cart) {
        const item = cart[key];
        if (typeof item === 'object' && item !== null && Object.prototype.hasOwnProperty.call(item, 'itemID') && item.itemID == itemID) {
            const limit = Number(item.itemInventory) || 1;
            if (item.itemQuantity < limit) {
                item.itemQuantity += 1;
            }
        }
    }
}
export function decreaseQuantityCart(cart, itemID) {
    for (const key in cart) {
        const item = cart[key];
        if (typeof item === 'object' && item !== null && Object.prototype.hasOwnProperty.call(item, 'itemID') && item.itemID == itemID) {
            if (item.itemQuantity > 1) {
                item.itemQuantity -= 1;
            }
        }
    }
}
export function getIndexInCart(cart, itemID) {
    for (let i = 0; i < cart.length; i++) {
        const item = cart[i];
        if (typeof item === 'object' && item !== null && Object.prototype.hasOwnProperty.call(item, 'itemID') && item.itemID == itemID) {
            return i;
        }
    }
    return -1;
}
export function getCartItemCount(cart) {
    let cartCount = 0;
    for (let i = 0; i < cart.length; i++) {
        const item = cart[i];
        if (typeof item === 'object' && item !== null && Object.prototype.hasOwnProperty.call(item, 'itemID')) {
            cartCount = cartCount + (item.itemQuantity);
        }
    }
    return cartCount;
}
export function getCartSubTotal(cart) {
    let cartSubTotal = 0;
    for (let i = 0; i < cart.length; i++) {
        const item = cart[i];
        if (typeof item === 'object' && item !== null &&
            Object.prototype.hasOwnProperty.call(item, 'itemQuantity') &&
            Object.prototype.hasOwnProperty.call(item, 'itemCost')) {
            cartSubTotal += (item.itemQuantity * item.itemCost);
        }
    }
    return formatAsHundredths(cartSubTotal);
}
function normalizeCartItemCost(item) {
    const value = item?.itemCost ?? item?.itemPrice ?? item?.unit_amount?.value;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
}
export function addToShoppingCart(thisItem) {
    let cart = getCart();
    const inventoryLimit = thisItem.itemInventory ?? 1;
    if (alreadyInCart(cart, thisItem.itemID)) {
        const index = getIndexInCart(cart, thisItem.itemID);
        // Sync latest inventory from source
        cart[index].itemInventory = inventoryLimit;
        if (!Number.isFinite(cart[index].itemCost)) {
            cart[index].itemCost = normalizeCartItemCost(thisItem);
        }
        if (cart[index].itemQuantity < inventoryLimit) {
            if (debug)
                console.log("Increasing quantity in cart");
            increaseQuantityCart(cart, thisItem.itemID);
        }
        else {
            if (debug)
                console.log("Cant add more than item quantity to the cart");
            // cant add more than quantity
        }
    }
    else {
        // BE SURE TO ADD ONLY ONE TO THE CART
        if (debug)
            console.log("Adding only one to the cart");
        const cartItem = { ...thisItem };
        cartItem.itemCost = normalizeCartItemCost(thisItem);
        cartItem.itemQuantity = 1;
        cartItem.itemInventory = inventoryLimit;
        cart.push(cartItem);
    }
    if (debug)
        console.debug('ShoppingCart:persisting cart -> CacheManager.set', shoppingCartKey, cart);
    cartCache.set(shoppingCartKey, cart);
    window.dispatchEvent(new Event('storage'));
}
export function increaseQuantityInCart(thisItem) {
    let cart = [...getCart()];
    const index = getIndexInCart(cart, thisItem.itemID);
    if (index !== -1) {
        cart[index] = { ...cart[index], itemQuantity: cart[index].itemQuantity + 1 };
        const limit = Number(cart[index].itemInventory) || 1;
        if (cart[index].itemQuantity > limit) {
            cart[index].itemQuantity = limit;
        }
    }
    if (debug)
        console.debug('ShoppingCart:increaseQuantityInCart -> persisting cart via CacheManager', shoppingCartKey, cart);
    cartCache.set(shoppingCartKey, cart);
    window.dispatchEvent(new Event('storage'));
}
export function decreaseQuantityInCart(thisItem) {
    let cart = [...getCart()];
    const index = getIndexInCart(cart, thisItem.itemID);
    if (index !== -1 && cart[index].itemQuantity > 1) {
        cart[index] = { ...cart[index], itemQuantity: cart[index].itemQuantity - 1 };
    }
    if (debug)
        console.debug('ShoppingCart:decreaseQuantityInCart -> persisting cart via CacheManager', shoppingCartKey, cart);
    cartCache.set(shoppingCartKey, cart);
    window.dispatchEvent(new Event('storage'));
}
export function removeFromShoppingCart(thisItem) {
    let cart = getCart().filter(item => item.itemID !== thisItem.itemID);
    if (debug)
        console.debug('ShoppingCart:removeFromShoppingCart -> persisting cart via CacheManager', shoppingCartKey, cart);
    cartCache.set(shoppingCartKey, cart);
    window.dispatchEvent(new Event('storage'));
}
export function clearShoppingCart() {
    if (debug)
        console.debug('ShoppingCart:clearShoppingCart -> using CacheManager.remove', shoppingCartKey, shippingInfoKey);
    cartCache.remove(shoppingCartKey);
    cartCache.remove(shippingInfoKey);
    window.dispatchEvent(new Event('storage'));
}
export function clearShoppingCartCache() {
    if (debug)
        console.debug('ShoppingCart:clearShoppingCartCache -> using CacheManager.clear');
    cartCache.clear();
}
/* ========== SHIPPING INFO FUNCTIONS ========== */
export function getShippingInfo() {
    if (debug)
        console.debug('ShoppingCart:getShippingInfo -> using CacheManager.get', shippingInfoKey);
    const cached = cartCache.get(shippingInfoKey);
    // Migration complete — don't read raw localStorage directly. Return empty when no data.
    return cached || {};
}
export function setShippingInfo(shippingFormData) {
    if (debug)
        console.debug('ShoppingCart:setShippingInfo -> using CacheManager.set', shippingInfoKey, shippingFormData);
    cartCache.set(shippingInfoKey, shippingFormData);
    window.dispatchEvent(new Event('storage'));
}
export function getCartShippingWeight(cart) {
    let totalWeight = 0;
    for (const item of cart) {
        if (!item || typeof item !== 'object')
            continue;
        const isShippable = item.itemIsShippable !== false;
        const weight = Number(item.itemWeight ?? 0);
        const quantity = Number(item.itemQuantity ?? 1);
        if (!isShippable || weight <= 0 || quantity <= 0)
            continue;
        const unit = item.itemWeightUnit;
        const normalizedUnit = String(unit || 'lb').trim().toLowerCase();
        const weightInPounds = normalizedUnit === 'lb' || normalizedUnit === 'lbs'
            ? weight
            : normalizedUnit === 'oz' || normalizedUnit === 'ounce' || normalizedUnit === 'ounces'
                ? weight / 16
                : normalizedUnit === 'g' || normalizedUnit === 'gram' || normalizedUnit === 'grams'
                    ? weight * 0.00220462
                    : weight;
        totalWeight += weightInPounds * quantity;
    }
    return formatAsHundredths(totalWeight);
}
export function getShippingInfoWithDefaults(defaultShippingInfo) {
    const currentInfo = getShippingInfo();
    if (!defaultShippingInfo || typeof defaultShippingInfo !== 'object') {
        return currentInfo;
    }
    return { ...defaultShippingInfo, ...currentInfo };
}
export function getShippingCost() {
    const ship = getShippingInfo();
    const explicitShippingCost = ship?.shippingCost;
    if (explicitShippingCost != null && explicitShippingCost !== '') {
        const parsed = Number(explicitShippingCost);
        if (Number.isFinite(parsed)) {
            return formatAsHundredths(parsed);
        }
    }
    return 0;
}
/* ========== CHECKOUT FUNCTIONS ========== */
export function getCheckoutData(defaultShippingInfo, shoppingcartConfig, subtotalDiscountCustom = 0) {
    const shippingTo = getShippingInfoWithDefaults(defaultShippingInfo);
    const itemCost = getCartSubTotal(getCart());
    const subtotalDiscount = getCartSubtotalDiscount(getCart(), subtotalDiscountCustom);
    const shippingCost = getShippingCost();
    const handlingFeeType = shoppingcartConfig?.handlingFeeType === 'percentage' ? 'percentage' : 'fixed';
    const handlingFeeAmount = Number(shoppingcartConfig?.handlingFeeAmount ?? 3.99);
    const handlingFee = handlingFeeType === 'percentage'
        ? formatAsHundredths((itemCost - subtotalDiscount + shippingCost) * (Number.isFinite(handlingFeeAmount) && handlingFeeAmount >= 0 ? handlingFeeAmount : 0))
        : formatAsHundredths(Number.isFinite(handlingFeeAmount) && handlingFeeAmount >= 0 ? handlingFeeAmount : 0);
    const checkoutCurrency = typeof (shoppingcartConfig?.handlingFeeCurrency || shoppingcartConfig?.currency || 'USD') === 'string' && String(shoppingcartConfig?.handlingFeeCurrency || shoppingcartConfig?.currency || 'USD').trim()
        ? String(shoppingcartConfig?.handlingFeeCurrency || shoppingcartConfig?.currency || 'USD').trim().toUpperCase()
        : 'USD';
    const salesTaxRateValue = Number(shoppingcartConfig?.taxRate);
    const salesTaxRate = Number.isFinite(salesTaxRateValue) && salesTaxRateValue >= 0 ? salesTaxRateValue : 0.06675;
    const salesTaxBase = itemCost - subtotalDiscount + shippingCost;
    const salesTax = formatAsHundredths(salesTaxRate * salesTaxBase);
    const checkoutTotal = formatAsHundredths(itemCost - subtotalDiscount + shippingCost + handlingFee + salesTax);
    const checkoutObj = {
        items: getCart(),
        subtotal: itemCost,
        subtotal_discount: subtotalDiscount,
        shippingTo,
        shippingCost,
        handlingFee,
        handlingFeeCurrency: checkoutCurrency,
        currency: checkoutCurrency,
        insuranceCost: undefined,
        shipping_discount: undefined,
        salesTax,
        total: checkoutTotal,
        shippingWeight: getCartShippingWeight(getCart()),
    };
    if (debug)
        console.log(checkoutObj);
    return checkoutObj;
}
/* ========== DISCOUNT CODE FUNCTIONS ========== */
export async function validateDiscountCode(field, apiProps) {
    try {
        const codeList = await getContentfulDiscountCodes({ apiProps: apiProps, contentType: "discountCodes" });
        if (!codeList) {
            return false;
        } // If no codes are found, return false
        if (field.value == '') {
            return true;
        } // If the field is empty, return true (no code entered)
        if (codeList.some((code) => code && code.codeName.toLowerCase() === field.value.toLowerCase())) {
            // if code is in the codeList
            const foundCode = codeList.find((code) => code.codeName.toLowerCase() === field.value.toLowerCase());
            if (foundCode) {
                // if code is active - between start and end date
                const startDate = new Date(foundCode.codeStart);
                const endDate = new Date(foundCode.codeEnd);
                const today = new Date();
                const isActive = today >= startDate && today <= endDate;
                return isActive;
            }
        }
        else {
            // if code is not in the codeList
            return false;
        }
    }
    catch (error) {
        console.error("Error fetching discount codes:", error); // Handle potential errors
        throw error; // Or return false;
    }
}
export async function getRemoteDiscountCodes(apiProps) {
    if (debug)
        console.log("Getting Contentful Discount Codes");
    try {
        const discountCodes = await getContentfulDiscountCodes({
            apiProps: apiProps,
            contentType: "discountCodes"
        });
        if (debug)
            console.log("Retrieved Contentful Discount Codes: ", discountCodes);
        return discountCodes;
    }
    catch (error) {
        console.error("An error occurred getting discount codes:", error);
    }
    ;
}
export function getLocalDiscountCodes() {
    if (debug)
        console.debug('ShoppingCart:getLocalDiscountCodes -> using CacheManager.get', discountCodesKey);
    const cached = cartCache.get(discountCodesKey);
    if (cached)
        return cached;
    // Do not read raw localStorage directly after migration — return empty when absent.
    return [];
}
export function setDiscountCodes(discountCodesJSON) {
    if (debug)
        console.debug("ShoppingCart:setDiscountCodes -> using CacheManager.set", discountCodesKey, discountCodesJSON);
    cartCache.set(discountCodesKey, discountCodesJSON);
    window.dispatchEvent(new Event('storage'));
}
export function getDiscountCode(codeString) {
    if (debug)
        console.log("Getting Discount Code Object");
    if (!codeString || codeString === '') {
        return undefined;
    } // If the code is empty, return null
    const discountCodes = getLocalDiscountCodes();
    if (!discountCodes) {
        return undefined;
    } // If no codes are found, return null
    // Find the discount code in the list
    const discountCode = discountCodes.find((code) => {
        if (code && code.codeName) {
            return code.codeName.toLowerCase() === codeString.toLowerCase();
        }
        else {
            return undefined;
        }
    });
    return discountCode;
}
export function getCartSubtotalDiscount(cart, subtotalDiscountCustom = 0) {
    if (!cart) {
        return 0;
    } // If cart is empty, return 0
    const cartSubTotal = getCartSubTotal(cart);
    const shippingInfo = getShippingInfo();
    const discountCode = getDiscountCode(shippingInfo.discountCode);
    let discountAmount = 0;
    if (discountCode) {
        if (discountCode.codeType === 'amount') {
            discountAmount = formatAsHundredths(discountCode.codeValue);
        }
        else if (discountCode.codeType === 'percent') {
            discountAmount = formatAsHundredths(cartSubTotal * discountCode.codeValue);
        }
    }
    const customDiscountAmount = Number(subtotalDiscountCustom);
    const additionalDiscount = Number.isFinite(customDiscountAmount) && customDiscountAmount > 0 ? formatAsHundredths(customDiscountAmount) : 0;
    return formatAsHundredths(discountAmount + additionalDiscount);
}
